import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { FormattedMessage } from 'react-intl'
import every from 'lodash/every'
import find from 'lodash/find'
import matches from 'lodash/matches'
import isNil from 'lodash/isNil'
import omit from 'lodash/omit'
import isEqual from 'lodash/isEqual'

import { intlShape } from '@/checkout-instore-legacy/i18n/intlShape'
import SelectedPayments from './SelectedPayments'
import {
  getAvailableGiftCards,
  getAvailablePaymentSystems,
  getOrderRemainingValue,
  getRemainingPayments,
  getPaymentSystemInstallments,
  recreatePaymentsBasedOnValueAndFixedPayments,
  arePaymentListsSimilar,
  hasVisiblePaymentSystems,
} from '@/checkout-instore-legacy/utils/PaymentUtils'
import SlideUpModal from '@/checkout-instore-legacy/components/Commons/Elements/SlideUpModal'
import AddPaymentForm from './AddPaymentForm'
import SectionTitle from '@/checkout-instore-legacy/components/Commons/Elements/SectionTitle'
import { logClickEvent } from '@/checkout-instore-legacy/utils/EventHelper'
import EditPaymentForm from './EditPaymentForm'
import { buildChildId } from '@/checkout-instore-legacy/utils/TestUtils'
import PaymentSystemSelector from './Selector/PaymentSystemSelector'
import FixedPayments from '@/checkout-instore-legacy/payment/components/FixedPayments'
import GiftCardSelector from './Selector/GiftCardSelector'
import TopMessage from '@/checkout-instore-legacy/components/Commons/TopMessage'
import Price from '@/checkout-instore-legacy/components/Commons/Price'

const NO_RENDER_PROPS = [
  'intl',
  'finishPendingOrder',
  'saveContext',
  'onHideAddPaymentModal',
  'refundPayment',
]

export default class PaymentSelection extends Component {
  constructor(props) {
    super(props)
    this.state = {
      indexOfPaymentBeingEdited: null,
    }
    this.paymentsWithFixedDirty = false
  }

  getTotalUsedValueInGiftCards() {
    return this.props.giftCards.reduce((sum, card) => sum + card.value, 0)
  }

  countUsedGiftCards() {
    return this.props.giftCards.reduce((sum, card) => sum + card.inUse, 0)
  }

  refreshInstallments() {
    if (every(this.props.payments, (payment) => payment.referenceValue > 0)) {
      // Send payments so it updates the number of installments (its important to use updatePayments so it count fixedPayments before send it)
      this.props.updatePayments(undefined, null, true)
    }
  }

  removePayment(paymentIndex) {
    logClickEvent('remove-payment', 'Sale')

    this.props.removePayment(paymentIndex)
    this.refreshInstallments()
  }

  removeGiftCard(giftCard) {
    logClickEvent('remove-giftcard', 'Sale')

    this.props.removeGiftCard(giftCard)
    this.refreshInstallments()
  }

  addPayment(paymentSystem, value) {
    logClickEvent('add-payment', 'Sale')

    const installments = getPaymentSystemInstallments(
      paymentSystem,
      this.props.installmentOptions
    )

    this.props.addPayment({
      paymentSystem: paymentSystem && paymentSystem.stringId,
      installments: installments && installments.length === 1 ? 1 : null,
      referenceValue: value,
    })
  }

  addGiftCard(giftCard) {
    logClickEvent('add-giftcard', 'Sale')
    this.props.addGiftCard(giftCard)
  }

  editPayment(paymentIndex, { value, installments }) {
    logClickEvent('edit-payment', 'Sale')

    const currentInstallments = this.props.payments[paymentIndex].installments
    if (!isNil(installments) && currentInstallments !== installments) {
      this.props.changePaymentSelectedInstallment(paymentIndex, installments)
    }
    if (!isNil(value)) {
      this.props.changePaymentValue(paymentIndex, value)
      this.refreshInstallments()
    }
  }

  removeAllSelectedPayments() {
    const giftCards = this.props.giftCards || []
    giftCards.forEach((giftCard) => {
      giftCard.inUse && this.props.removeGiftCard(giftCard)
    })
    this.props.removeAllPayments()
  }

  onSelectGiftCard(giftCard) {
    if (!giftCard.inUse) {
      this.removeAllSelectedPayments()
      this.addGiftCard(giftCard)
    }
  }

  onSelectPaymentSystem(paymentSystem) {
    const selectedPayment = this.getSelectedPayment() || {}
    const selectedPaymentSystemId = selectedPayment.paymentSystem
    if (selectedPaymentSystemId !== String(paymentSystem.id)) {
      this.removeAllSelectedPayments()
      this.addPayment(paymentSystem, this.props.value)
    }
  }

  getSelectedPayment() {
    const { payments, giftCards } = this.props
    const giftCard = find(giftCards, matches({ inUse: true })) || undefined
    return (payments && payments.length && payments[0]) || giftCard
  }

  async handleAddPayment(payment, paymentType, value) {
    if (paymentType === 'payment-system') {
      await this.addPayment(payment, value)
    } else if (paymentType === 'gift-card') {
      this.addGiftCard(payment)
    }
    this.refreshInstallments()
    this.props.onHideAddPaymentModal()
  }

  getPaymentBeingEdited() {
    return !isNil(this.state.indexOfPaymentBeingEdited)
      ? this.props.payments[this.state.indexOfPaymentBeingEdited]
      : null
  }

  getIsSelectedComparator() {
    const selectedPayment = this.getSelectedPayment()
    if (selectedPayment && selectedPayment.paymentSystem) {
      const selectedId = selectedPayment.paymentSystem
      return (paymentSystem) => String(paymentSystem.id) === selectedId
    }
    return (giftCard) => selectedPayment === giftCard
  }

  renderPaymentActions({ index, giftCard, parentId }) {
    const paymentType = giftCard ? 'gift-card' : 'payment-system'

    if (paymentType === 'payment-system') {
      if (this.props.payments.length > 1) {
        return (
          <div className="flex-none self-center f3 f2-ns ph4 ph5-ns ph6-l">
            <i
              id={buildChildId('edit', parentId)}
              className="pointer c-action-primary hover-c-action-primary fa fa-edit f2-ns"
              onClick={() =>
                this.setState({
                  indexOfPaymentBeingEdited: index,
                })
              }
            />
          </div>
        )
      }
      return (
        <div className="flex-none self-center f3 f2-ns ph4 ph5-ns ph6-l">
          <i
            id={buildChildId('remove', parentId)}
            className="pointer light-red hover-red fa fa-cancel f2-ns"
            onClick={() => this.removePayment(index)}
          />
        </div>
      )
    } else if (paymentType === 'gift-card') {
      return (
        <div className="flex-none self-center f3 f2-ns ph4 ph5-ns ph6-l">
          <i
            id={buildChildId('remove', parentId)}
            className="pointer light-red hover-red fa fa-cancel f2-ns"
            onClick={() => this.removeGiftCard(giftCard)}
          />
        </div>
      )
    }
  }

  renderGiftCardIsNotEnough({
    numberOfUsedGiftCards,
    numberOfPayments,
    valueAfterGiftCards,
  }) {
    const { intl } = this.props
    return (
      <TopMessage
        type="info"
        isVisible={
          numberOfUsedGiftCards === 1 &&
          numberOfPayments === 0 &&
          valueAfterGiftCards > 0
        }
      >
        <FormattedMessage
          id="completePaymentWarning"
          values={{
            newPayment: (
              <span className="fw4">
                {intl.formatMessage({
                  id: 'newPayment',
                })}
              </span>
            ),
          }}
        />
      </TopMessage>
    )
  }

  renderRemainingValueIsNotZero({
    numberOfUsedGiftCards,
    numberOfPayments,
    remainingValue,
  }) {
    if (numberOfUsedGiftCards + numberOfPayments >= 2) {
      return (
        <div>
          <TopMessage type="warning" isVisible={remainingValue < 0}>
            <FormattedMessage id="paymentSelectionTheValueIs" />{' '}
            <span className="fw4">
              <Price value={-remainingValue} />
            </span>{' '}
            <FormattedMessage id="paymentSelectionGreaterThanTotal" />
          </TopMessage>

          <TopMessage
            type="warning"
            isVisible={
              remainingValue > 0 && remainingValue !== this.props.value
            }
          >
            <FormattedMessage id="paymentSelectionTheValueIs" />{' '}
            <span className="fw4">
              <Price value={remainingValue} />
            </span>{' '}
            <FormattedMessage id="paymentSelectionBelowTotal" />
          </TopMessage>
        </div>
      )
    }
  }

  arePaymentsWithFixedPaymentsTheSame(
    payments,
    giftCards,
    fixedPayments,
    value
  ) {
    const newPayments = recreatePaymentsBasedOnValueAndFixedPayments(
      value,
      giftCards,
      payments,
      null,
      fixedPayments
    )
    return arePaymentListsSimilar(payments, newPayments)
  }

  shouldComponentUpdate(nextProps, nextState) {
    const valuesUsedOnRender = {
      ...omit(this.props, NO_RENDER_PROPS),
      ...this.state,
    }

    const nextValuesUsedOnRender = {
      ...omit(nextProps, NO_RENDER_PROPS),
      ...nextState,
    }

    return !isEqual(valuesUsedOnRender, nextValuesUsedOnRender)
  }

  componentDidMount() {
    const { payments, fixedPayments, success } = this.props

    const hasFixedPayment = fixedPayments && fixedPayments.length > 0

    this.props.updatePaymentsByFixed(
      payments,
      hasFixedPayment ? fixedPayments : []
    )

    if (success) {
      this.props.finishPendingOrder()
    }

    this.props.saveContext()
  }

  componentDidUpdate(prevProps) {
    if (this.props.success && !prevProps.success) {
      this.props.finishPendingOrder()
    }

    if (
      prevProps.fixedPayments &&
      this.props.fixedPayments &&
      this.props.fixedPayments.length > 0
    ) {
      const paymentsWithFixedDirty = !this.arePaymentsWithFixedPaymentsTheSame(
        this.props.payments,
        this.props.giftCards,
        this.props.fixedPayments,
        this.props.value
      )

      if (!this.paymentsWithFixedDirty && paymentsWithFixedDirty) {
        this.props.updatePaymentsByFixed(
          this.props.payments,
          this.props.fixedPayments
        )
      }

      this.paymentsWithFixedDirty = paymentsWithFixedDirty
    }
  }

  render() {
    const {
      value,
      payments,
      paymentSystems,
      giftCards,
      installmentOptions,
      fixedPayments,
      paymentsPaid,
      finishPendingOrder,
      splitPayments,
      totalsPerSeller,
      sellersWithoutPermission,
      refundPayment,
      success,
      intl,
    } = this.props

    const availablePaymentSystems = getAvailablePaymentSystems(
      paymentSystems,
      payments
    )

    const availableGiftCards = getAvailableGiftCards(giftCards)

    const numberOfPayments = payments ? payments.length : 0
    const numberOfUsedGiftCards = this.countUsedGiftCards()

    const valueAfterGiftCards = value - this.getTotalUsedValueInGiftCards()
    const remainingValue = getOrderRemainingValue(value, payments, giftCards)

    // If we have multiple payments, none can equal or surpass the order value
    const maxValue = valueAfterGiftCards - (numberOfPayments > 0)
    const initialValue =
      numberOfPayments === 1 ? valueAfterGiftCards / 2 : remainingValue

    const paymentsWithoutFixed = getRemainingPayments(payments, fixedPayments)

    const newPaymentLabel = intl.formatMessage({
      id: 'newPayment',
    })

    const hasFixedPayment = fixedPayments && fixedPayments.length > 0
    const fixedPaymentsElm = (
      <FixedPayments
        payments={paymentsWithoutFixed}
        fixedPayments={fixedPayments}
        remainingValue={remainingValue}
        finishPendingOrder={finishPendingOrder}
        success={success}
      />
    )
    const paymentSystemsVisible = hasVisiblePaymentSystems(
      paymentSystems,
      payments,
      giftCards
    )

    return (
      <div id={this.props.id}>
        {this.renderGiftCardIsNotEnough({
          numberOfUsedGiftCards,
          numberOfPayments,
          valueAfterGiftCards,
        })}
        {this.renderRemainingValueIsNotZero({
          numberOfUsedGiftCards,
          numberOfPayments,
          remainingValue,
        })}

        {numberOfPayments + numberOfUsedGiftCards <= 1 ? (
          hasFixedPayment && !splitPayments ? (
            fixedPaymentsElm
          ) : (
            <div>
              {paymentSystemsVisible && (
                <SectionTitle>
                  <FormattedMessage id="selectPayment" />
                </SectionTitle>
              )}

              <GiftCardSelector
                id={buildChildId('giftcards', this.props.id)}
                selected={this.getIsSelectedComparator()}
                giftCards={giftCards}
                onSelect={(giftCard) => this.onSelectGiftCard(giftCard)}
              />

              <PaymentSystemSelector
                id={buildChildId('payments', this.props.id)}
                value={value}
                selected={this.getIsSelectedComparator()}
                paymentSystems={paymentSystems}
                onSelect={(ps) => this.onSelectPaymentSystem(ps)}
                installmentOptions={installmentOptions}
                payments={payments}
                splitPayments={splitPayments}
                totalsPerSeller={totalsPerSeller}
                sellersWithoutPermission={sellersWithoutPermission}
                paymentsPaid={paymentsPaid}
                onRefund={refundPayment}
                onSelectInstallment={(payment, index, installments) =>
                  this.editPayment(index, { installments })
                }
              />
            </div>
          )
        ) : (
          <div>
            {fixedPaymentsElm}
            <SectionTitle>
              <FormattedMessage id="selectedPayments" />
            </SectionTitle>
            <SelectedPayments
              id={buildChildId('selected', this.props.id)}
              payments={paymentsWithoutFixed}
              initialIndex={hasFixedPayment ? fixedPayments.length : 0}
              giftCards={giftCards}
              installmentOptions={installmentOptions}
              paymentSystems={paymentSystems}
              onSelectInstallment={(payment, index, installments) =>
                this.editPayment(index, { installments })
              }
              renderPaymentActions={(...args) =>
                this.renderPaymentActions(...args)
              }
            />
          </div>
        )}

        <SlideUpModal
          title={newPaymentLabel}
          isOpen={this.props.showAddPaymentModal}
          onClose={this.props.onHideAddPaymentModal}
        >
          <AddPaymentForm
            id={buildChildId('add-payment-form', this.props.id)}
            giftCards={availableGiftCards}
            paymentSystems={availablePaymentSystems}
            installmentOptions={installmentOptions}
            initialValue={initialValue}
            remainingValue={numberOfPayments >= 2 ? remainingValue : maxValue}
            maxValue={maxValue}
            hideValueInput={numberOfPayments === 0}
            onSubmit={(...args) => this.handleAddPayment(...args)}
            sellersWithoutPermission={sellersWithoutPermission}
          />
        </SlideUpModal>

        <SlideUpModal
          title="Editar pagamento"
          isOpen={typeof this.state.indexOfPaymentBeingEdited === 'number'}
          onClose={() => this.setState({ indexOfPaymentBeingEdited: null })}
        >
          <EditPaymentForm
            id={buildChildId('edit-payment-form', this.props.id)}
            paymentSystems={paymentSystems}
            payment={this.getPaymentBeingEdited()}
            remainingValue={numberOfPayments > 2 ? remainingValue : maxValue}
            maxValue={maxValue}
            onSubmit={(value) => {
              this.editPayment(this.state.indexOfPaymentBeingEdited, { value })
              this.setState({ indexOfPaymentBeingEdited: null })
            }}
            onDelete={() => {
              this.removePayment(this.state.indexOfPaymentBeingEdited)
              this.setState({ indexOfPaymentBeingEdited: null })
            }}
          />
        </SlideUpModal>
      </div>
    )
  }
}

PaymentSelection.propTypes = {
  id: PropTypes.string,
  giftCards: PropTypes.array,
  installmentOptions: PropTypes.array,
  paymentSystems: PropTypes.array,
  payments: PropTypes.array,
  fixedPayments: PropTypes.array,
  paymentsPaid: PropTypes.array,
  success: PropTypes.bool,
  value: PropTypes.number,
  showAddPaymentModal: PropTypes.bool,
  splitPayments: PropTypes.bool.isRequired,
  totalsPerSeller: PropTypes.object.isRequired,
  sellersWithoutPermission: PropTypes.arrayOf(PropTypes.string).isRequired,
  refundPayment: PropTypes.func,
  intl: intlShape.isRequired,
  finishPendingOrder: PropTypes.func.isRequired,
  saveContext: PropTypes.func.isRequired,
  onHideAddPaymentModal: PropTypes.func,
  updatePayments: PropTypes.func.isRequired,
  removeGiftCard: PropTypes.func.isRequired,
  removePayment: PropTypes.func.isRequired,
  addPayment: PropTypes.func.isRequired,
  addGiftCard: PropTypes.func.isRequired,
  changePaymentSelectedInstallment: PropTypes.func.isRequired,
  changePaymentValue: PropTypes.func.isRequired,
  removeAllPayments: PropTypes.func.isRequired,
  updatePaymentsByFixed: PropTypes.func.isRequired,
}
